import { formatMoney } from "@/lib/formatters"
import { t } from "@/lib/i18n"

/**
 * Shape, endpoints and copy lookup for taking money off a bill.
 *
 * **The browser decides nothing about a discount.** Whether one is allowed, how
 * much could come off, and whether it takes effect now or waits for a manager
 * are all answered by `Location::DiscountEligibility` on the server — asked once
 * by `Staff::TablesWorkspaceSerializer` for this screen and again by
 * `Location::DiscountApplyService` inside the bill lock for the write. This
 * module reads those answers; it never re-derives them. The rule is the one the
 * refund feature already states: a figure the restaurant will argue about later
 * has to have exactly one author.
 *
 * ## Three kinds, and one of them is not a percentage
 *
 * `percentage` and `fixed_amount` take a number from the operator. `courtesy`
 * does not: `Location::DiscountedLine` makes it the whole line by construction,
 * because `Location::DiscountPolicy` rule 2 says a courtesy is a giveaway rather
 * than a generous discount — it must never count as revenue. So the value field
 * disappears entirely for a courtesy instead of being pre-filled with 100, which
 * would invite someone to type 90 and land back in the shape rule 2 forbids.
 *
 * ## What the operator typed, and what gets posted
 *
 * `value` travels in the operator's own units — a plain percentage, or **pesos**
 * for a fixed amount. `Location::DiscountApplyService` converts to cents at its
 * boundary. The peso amount is normalised through integer arithmetic here for
 * the same reason `refundAmountParam` is: it feeds
 * `Digest::SHA256.hexdigest([... parsed_value.to_s("F") ...])`, and a centavo of
 * binary-float dust is a different idempotency key and therefore a second
 * discount.
 */

export type DiscountKind = "percentage" | "fixed_amount" | "courtesy"

/**
 * `pending` and `applied` are live — they hold the line against a second
 * discount. `rejected` and `voided` released it, and stay only as the record of
 * what was asked for. `superseded` is a rule a person replaced: it released the
 * line too, and it is spelled apart from `voided` so an auditor can tell a
 * promotion somebody undid from one a manual discount displaced.
 */
export type DiscountStatus = "pending" | "applied" | "rejected" | "voided" | "superseded"

/**
 * Who filed the row. `manual` is a person who signed for it; the other two are
 * the system applying a standing rule, with no actor by construction.
 */
export type DiscountSource = "manual" | "promotion" | "coupon"

/**
 * What a manual discount would take off before it is even applied.
 *
 * A person's decision supersedes a rule's, so applying a discount over a line a
 * promotion is standing on removes that promotion. **A bill-level promotion
 * comes off wholesale** — there is no partial-void path on the server — so
 * comping one dish can cost the table its whole happy hour.
 *
 * `amount` is the server's figure in pesos, never recomputed here: the ceiling
 * rule of this module applies to what a discount destroys exactly as it applies
 * to what a discount takes.
 */
export type DiscountSupersede = {
  /** Exact decimal string. The pesos of discount the table stops getting. */
  amount: string
  /** How many rules come off. */
  count: number
  /** Their names, deduped. Empty only if a rule lost its promotion row. */
  names: string[]
  /** True when one of them covers the whole account, not just this line. */
  billLevel: boolean
}

/** `orders[].discountEligibility`, and the bill-level answer with no order attached. */
export type DiscountEligibility = {
  allowed: boolean
  /** The ceiling, in integer cents, decided server-side. */
  maxDiscountableCents: number
  /** Why a discount is refused. `null` when it is allowed. */
  reasonCode: string | null
  /**
   * Whether this person's discount would land `pending` instead of taking
   * effect. The dialog says so before the operator commits: promising a diner a
   * discount and then discovering it needs a manager is the failure
   * `docs/product.md` forbids.
   */
  requiresAuthorization: boolean
  /**
   * The promotions this discount would displace, or `null` when there are none.
   * Optional so a payload from an older serializer renders the dialog as it
   * always did instead of crashing on `.amount` of undefined.
   */
  supersedes?: DiscountSupersede | null
}

/**
 * A row of `Location::Discount` — the ledger, pending and resolved alike.
 *
 * `amount` is `0.00` while pending, and that is not a rendering bug: a discount
 * waiting for a second pair of eyes has reduced nothing. What the operator asked
 * for lives in `value`, which is why the pending strip shows that figure rather
 * than the amount.
 */
export type AppliedDiscount = {
  id: number
  status: DiscountStatus
  kind: string
  kindLabel: string
  reason: string
  reasonLabel: string
  /** Exact decimal string, the way every other amount on this screen arrives. */
  amount: string
  /** What the operator typed: a percentage, or a peso amount. */
  value: string
  /**
   * Who filed it. Absent on a payload from an older serializer, and read as
   * `manual` when it is: that is the conservative direction, because a manual
   * row is the one that holds the line against a second discount.
   */
  source?: DiscountSource | null
  /** The rule behind a system row. Null for a manual discount. */
  promotionName?: string | null
  actorName?: string | null
  authorizedByName?: string | null
  /** `null` for a bill-level discount, whose split lives per line on the server. */
  orderId?: number | null
  /** The dish the discount came off, when it came off one. */
  orderName?: string | null
  createdAt?: string | null
  resolvedAt?: string | null
}

export type DiscountReasonOption = {
  value: string
  label: string
}

export type DiscountKindOption = {
  value: string
  label: string
}

/**
 * Create is nested under the bill it reduces; the three resolutions address the
 * discount row itself. `approve` / `reject` mirror the entry-request pair, the
 * app's only other two-actor approval shape.
 */
export const DISCOUNT_PATHS = {
  create: (billId: number) => `/bills/${billId}/discounts`,
  approve: (discountId: number) => `/discounts/${discountId}/approve`,
  reject: (discountId: number) => `/discounts/${discountId}/reject`,
  void: (discountId: number) => `/discounts/${discountId}`,
} as const

/** The location feature that has to be on for the endpoint to answer at all. */
export const DISCOUNTS_FEATURE = "discounts"

/** The one kind that takes no value from the operator. */
export const COURTESY_KIND = "courtesy"

const I18N_PREFIX = "ui.react.features.staff.discounts"

export function tDiscount(key: string, options: Record<string, string | number> = {}) {
  return t(`${I18N_PREFIX}.${key}`, options)
}

/**
 * Every refusal `Location::DiscountEligibility` can report, named.
 *
 * Unlike the refund dialog, none of these fall through to the generic line by
 * design: a discount is refused for reasons the operator can act on — a charge
 * in flight, a line already discounted, a bill being split — and "no se puede"
 * sends a waiter looking for a button instead of at the till. `blocked_generic`
 * survives only for a code this build does not know, which is a payload from a
 * newer server rather than a state anyone can reach today.
 */
const BLOCKED_KEYS: Record<string, string> = {
  bill_not_open: "blocked_bill_not_open",
  bill_cancelled: "blocked_bill_cancelled",
  payment_in_flight: "blocked_payment_in_flight",
  split_settling: "blocked_split_settling",
  line_paid: "blocked_line_paid",
  line_locked: "blocked_line_locked",
  line_cancelled: "blocked_line_cancelled",
  already_discounted: "blocked_already_discounted",
  nothing_to_discount: "blocked_nothing_to_discount",
  invalid_kind: "blocked_invalid_kind",
  invalid_reason: "blocked_invalid_reason",
  exceeds_line: "blocked_exceeds_line",
  non_positive: "blocked_non_positive",
}

export function discountBlockedKey(reasonCode: string | null | undefined): string {
  if (!reasonCode) return "blocked_generic"

  return BLOCKED_KEYS[reasonCode] ?? "blocked_generic"
}

/** Which label the value field wears. A courtesy has no value field at all. */
export function discountValueLabelKey(kind: string): string {
  return kind === "fixed_amount" ? "value_label_fixed" : "value_label_percentage"
}

/**
 * Cents to the decimal string the endpoint parses.
 *
 * Integer arithmetic rather than `(cents / 100).toFixed(2)`, exactly as
 * `refundAmountParam` does and for the same reason: the value posted here is
 * hashed into the idempotency key, so a centavo of drift files a second discount
 * instead of replaying the first.
 */
export function discountAmountParam(cents: number): string {
  if (!Number.isFinite(cents)) return "0.00"

  const whole = Math.trunc(Math.abs(cents))
  const sign = cents < 0 ? "-" : ""

  return `${sign}${Math.floor(whole / 100)}.${String(whole % 100).padStart(2, "0")}`
}

/**
 * What the operator typed, as cents.
 *
 * `Number("12.34") * 100` is `1233.9999999999998`; rounding is what makes the
 * peso amount survive the trip. A field the operator left empty, or filled with
 * something that is not a number, is zero here and refused by the confirm
 * button — never posted as `NaN` for the server to interpret.
 */
export function pesosToCents(raw: string): number {
  const parsed = Number(String(raw).trim())

  return Number.isFinite(parsed) ? Math.round(parsed * 100) : 0
}

/**
 * The `value` param, in the units `Location::DiscountApplyService` documents.
 *
 * A courtesy posts `0`: the service ignores the value for that kind, and sending
 * the line's amount instead would put a figure in the idempotency key that the
 * operator never chose.
 */
export function discountValueParam(kind: string, raw: string): string {
  if (kind === COURTESY_KIND) return "0"
  if (kind === "fixed_amount") return discountAmountParam(pesosToCents(raw))

  const parsed = Number(String(raw).trim())

  return Number.isFinite(parsed) ? String(parsed) : "0"
}

/** Whether the confirm button has everything the endpoint needs. */
export function discountRequestComplete(kind: string, value: string, reason: string): boolean {
  if (!kind || !reason) return false
  if (kind === COURTESY_KIND) return true

  return pesosToCents(value) > 0
}

/**
 * What a discount asked for, in one phrase.
 *
 * The pending strip shows this rather than `amount`, which is `0.00` until a
 * manager decides — a manager reading "0.00" would be deciding on a figure the
 * screen invented. A percentage reads as a percentage, a fixed amount as money,
 * and a courtesy says so by name because it has no number of its own.
 */
export function discountRequestLabel(
  discount: Pick<AppliedDiscount, "kind" | "kindLabel" | "value">,
  currency: string
): string {
  if (discount.kind === COURTESY_KIND) return discount.kindLabel
  if (discount.kind === "fixed_amount") return formatMoney(discount.value, { currency })

  const parsed = Number(discount.value)

  return `${Number.isFinite(parsed) ? parsed : 0}%`
}

/** Whether an order line actually carries money taken off it. */
export function discountedLine(discountAmount: string | null | undefined): boolean {
  return Number(discountAmount ?? 0) > 0
}

/**
 * Whether this row is a rule the system applied rather than a person's decision.
 *
 * An absent `source` reads as manual, which is the conservative answer: a
 * payload from an older serializer carries only manual rows, and treating one as
 * a rule would offer a second discount over a line that already holds one.
 */
export function systemDiscount(
  discount: Pick<AppliedDiscount, "source"> | null | undefined
): boolean {
  const source = discount?.source
  return source != null && source !== "manual"
}

/**
 * What a discount row calls itself on the ticket.
 *
 * A rule names itself — "Happy hour" is the thing the diner was promised and the
 * thing the manager is about to remove. A manual discount has no name, so it
 * falls back to its reason, which is the catalog entry a person chose and the
 * one the control report groups on.
 */
export function discountSourceLabel(
  discount: Pick<AppliedDiscount, "source" | "promotionName" | "reasonLabel">
): string {
  if (systemDiscount(discount) && discount.promotionName) return discount.promotionName

  return discount.reasonLabel
}

/**
 * Which sentence the confirm dialog owes the operator, given what comes off.
 *
 * Three, not one, because the wholesale case is the one nobody expects: a
 * bill-wide promotion superseded by a one-line discount is removed from EVERY
 * line, and "no solo de este producto" is the whole point of saying it out loud.
 */
export function supersedeNoticeKey(
  supersede: DiscountSupersede,
  scope: "line" | "bill"
): string {
  if (!supersede.billLevel) return "supersede_lines"

  return scope === "line" ? "supersede_line_wholesale" : "supersede_wholesale"
}

/**
 * The rules being removed, as one phrase.
 *
 * A row whose promotion is missing — archived out from under it, or a coupon
 * whose rule was renamed away — still has to be named, or the sentence reads
 * "applying this removes  from the whole bill".
 */
export function supersedeNames(supersede: DiscountSupersede): string {
  if (supersede.names.length === 0) return tDiscount("supersede_unnamed")

  return supersede.names.join(" · ")
}
